// Receive order/service notifications broadcast by the e-menu-platform.
//
// This mirrors the platform's
//   app/assets/javascripts/channels/notifications/notifications.js
// but the menu-builder has no Noticed models or channels of its own — it simply
// connects cross-app to the platform's ActionCable (the same approach
// pos/payment_container_subscription.js already uses) and subscribes to the
// platform's Noticed::NotificationChannel. The platform sends fully-rendered
// `html`, so no partials/models are needed on this side.
//
// We import jQuery directly rather than relying on the global `window.$`:
// ES `import` statements are hoisted, so this module evaluates before
// packs/application.js runs its inline `window.$ = JQuery` assignment.
// webpack dedupes `jquery` to a single instance — the same one
// `import "bootstrap"` extends with `.toast()` — so this binding has the
// toast plugin available.

import $ from "jquery"
import { createConsumer } from "@rails/actioncable"

let consumer = null

// Bootstrap toasts that must stay open until the user dismisses them.
const toastStickyOptions = { autohide: false }

function getWssUrl(platformUrl) {
  return platformUrl.replace(/^http/, "ws") + "/cable"
}

function notificationAction($container) {
  return $container.data("show-notifications")
}

function addUnreadNotificationDot() {
  $("#unread-notification-dot").removeClass("d-none")
}

function removeUnreadNotificationDot() {
  $("#unread-notification-dot").addClass("d-none")
}

// Notification sound (mirror of the platform's bell). Plain HTML5 Audio — no
// Howler dependency. The asset lives in the menu-builder's public/ folder.
let notificationAudio = null
function getNotificationAudio() {
  if (!notificationAudio) notificationAudio = new Audio("/order-bell.mp3")
  return notificationAudio
}

// Audio is "enabled" once the user clicks "Temporarily Enable" in the audio
// popover (which both flips the bubble to the unmuted icon and provides the
// user gesture browsers require before media can play).
function audioEnabled() {
  return window.__emenuAudioEnabled === true || $("#audio-popover").is(":visible")
}

// The platform persists "audio enabled" in a (non-HttpOnly, domain-shared)
// cookie named "<prefix>emenu_audio_autoplay_set". Because it's set on the
// localhost / .emenunow.com domain, the menu-builder can read it too, so the
// mute/unmute bubble can start in the same state as the platform.
function platformAudioCookieSet() {
  return document.cookie.split(";").some((c) => {
    const name = c.split("=")[0].trim()
    return /emenu_audio_autoplay_set$/.test(name)
  })
}

function syncAudioMuteState() {
  if (platformAudioCookieSet()) {
    window.__emenuAudioEnabled = true
    $(".audio-muted-icon").hide()
    $(".audio-icon").show()
  }
}

function playNotificationAudio() {
  if (!audioEnabled()) return
  const audio = getNotificationAudio()
  try {
    audio.currentTime = 0
    const p = audio.play()
    if (p && p.catch) p.catch(() => {})
  } catch (e) {
    /* ignore autoplay restrictions */
  }
}

// The platform calls $("time").timeago() (jquery-timeago plugin), which the
// menu-builder doesn't bundle. The notification HTML ships a <time datetime>
// element, so format the relative phrase ourselves to match the platform's
// "less than a minute ago" style.
function relativeFromNow(date) {
  const seconds = Math.max(0, (Date.now() - date.getTime()) / 1000)
  const minutes = seconds / 60
  const hours = minutes / 60
  const days = hours / 24
  const round = Math.round

  if (seconds < 45) return "less than a minute ago"
  if (seconds < 90) return "about a minute ago"
  if (minutes < 45) return `${round(minutes)} minutes ago`
  if (minutes < 90) return "about an hour ago"
  if (hours < 24) return `about ${round(hours)} hours ago`
  if (hours < 42) return "a day ago"
  if (days < 30) return `${round(days)} days ago`
  if (days < 45) return "about a month ago"
  if (days < 365) return `${round(days / 30)} months ago`
  if (days < 547) return "about a year ago"
  return `over ${round(days / 365)} years ago`
}

function applyTimeago() {
  if ($.fn && $.fn.timeago) {
    $("#notification-container time").timeago()
    return
  }
  $("#notification-container time[datetime]").each(function () {
    const iso = $(this).attr("datetime")
    const date = new Date(iso)
    if (!isNaN(date.getTime())) $(this).text(relativeFromNow(date))
  })
}

// The platform renders these links with root-relative paths (/manager/...),
// which would resolve against the menu-builder origin once injected here. Point
// them back at the platform. Also neutralise the Dismiss link's rails-ujs
// attributes so we can fire it ourselves cross-origin (see the click handler).
function absolutizeNotificationUrls($container, platformUrl) {
  $container.find("a[href^='/']").each(function () {
    const href = $(this).attr("href")
    // Only rewrite root-relative (/...), not protocol-relative (//host).
    if (href && href.charAt(0) === "/" && href.charAt(1) !== "/") {
      $(this).attr("href", platformUrl + href)
    }
  })
  $container
    .find("a[data-dismiss='toast'][data-remote]")
    .removeAttr("data-remote")
    .removeAttr("data-method")
}

// Load the current notifications from the platform (its DB state) and render
// them. This is what gives parity with the platform on page load / refresh:
// the platform server-renders existing unread notifications, and here we fetch
// the same consolidated partial. The credentialed request works because the
// platform's CORS now allows the menu-builder origin with credentials (the
// session cookie rides the same-site request).
function loadConsolidatedNotifications(platformUrl, $container) {
  return fetch(platformUrl + "/manager/notifications/consolidated", {
    method: "GET",
    credentials: "include",
    headers: { Accept: "text/html" },
  })
    .then((res) => {
      if (res.status === 204) {
        // No unread notifications.
        $("#consolidated-notifications").remove()
        removeUnreadNotificationDot()
        return null
      }
      if (!res.ok) return null
      return res.text()
    })
    .then((html) => {
      if (!html) return
      $container.html(html)
      absolutizeNotificationUrls($container, platformUrl)
      addUnreadNotificationDot()
      applyTimeago()
    })
    .catch((err) => {
      console.error("Failed to load platform notifications:", err)
    })
}

export function subscribeToPlatformNotifications() {
  const $container = $("#notification-container")
  if ($container.length === 0) return

  const restaurantId = $container.data("restaurant-id")
  const platformUrl = $container.data("platform-url")
  if (!platformUrl || !restaurantId) return

  // Match the platform's mute/unmute state (read from the shared cookie).
  syncAudioMuteState()

  // Show existing notifications immediately, like the platform does on load.
  loadConsolidatedNotifications(platformUrl, $container)

  // Dismiss: the platform's link is a rails-ujs remote POST to a (now absolute)
  // platform URL. We neutralised its ujs attrs in absolutizeNotificationUrls, so
  // fire it ourselves with credentials, then refetch to reflect the new state.
  $(document).on("click", "#notification-container a[data-dismiss='toast']", function (event) {
    const url = $(this).attr("href")
    if (!url) return
    event.preventDefault()
    fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { Accept: "*/*" },
    })
      .then(() => loadConsolidatedNotifications(platformUrl, $container))
      .catch((err) => console.error("Failed to dismiss notifications:", err))
  })

  if (!consumer) {
    consumer = createConsumer(getWssUrl(platformUrl))
  }

  consumer.subscriptions.create(
    { channel: "Noticed::NotificationChannel", restaurant_id: restaurantId },
    {
      connected() {
        console.log("Connected to platform Noticed::NotificationChannel")
      },

      disconnected() {
        console.log("Disconnected from platform Noticed::NotificationChannel")
      },

      received(data) {
        console.log("Notification received from platform:", data)

        // A notification was read/closed/destroyed on the platform — drop the
        // toast and refetch the consolidated panel so the count stays in sync
        // (mirrors the platform's dismiss handler).
        if (data["message"] == "dismiss") {
          $(`#notification-${data["notification_id"]}`).remove()
          loadConsolidatedNotifications(platformUrl, $container)
        } else if (data["message"] == "refresh") {
          loadConsolidatedNotifications(platformUrl, $container)
        } else if (data["restaurant_id"] == restaurantId) {
          const notificationHtml = data["html"]

          // Consolidated notifications replace the whole panel; individual ones
          // are prepended so the newest sits on top.
          if (data["consolidate"] == true) {
            $container.html(notificationHtml)
          } else {
            $container.prepend(notificationHtml)
          }

          absolutizeNotificationUrls($container, platformUrl)
          $(`#notification-${data["notification_id"]}`)
            .toast(toastStickyOptions)
            .toast(notificationAction($container))
          addUnreadNotificationDot()
          applyTimeago()
          if (notificationAction($container) == "show") playNotificationAudio()
        }
      },
    }
  )
}

$(function () {
  subscribeToPlatformNotifications()
})
